import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, Heart, Sparkles } from 'lucide-react';
import BreathingRoomAttribution from '../components/BreathingRoomAttribution';

const BOOKING_URL = "https://breathingroombda.com/appointments-2/";

const HomePage = () => {
  const fadeUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  };

  const staggerContainer = {
    animate: { transition: { staggerChildren: 0.15 } }
  };

  const pillars = [
    {
      number: '01',
      title: 'Restorative Movement',
      description: 'Meeting your body where it is. Developing functional movement and a healthy connection through various modalities including Human Garage Fascial Maneuvers.',
      icon: Leaf,
    },
    {
      number: '02',
      title: 'Holistic Health Coaching',
      description: 'Foods, sleep, stress, digestion, detox. Using metabolic typing and gut healing protocols to synchronize your choices toward supporting your health.',
      icon: Heart,
    },
    {
      number: '03',
      title: 'Holistic Coaching (IMESP)',
      description: 'Deeper self-work through 5 realms: Intuitive, Mental, Emotional, Spiritual, Physical. We are physical beings having a spiritual existence.',
      icon: Sparkles,
    },
  ];

  const testimonials = [
    {
      quote: "Working with Jill helped me reconnect with my body in ways I never thought possible. Her grounded approach was exactly what I needed.",
      name: "Sarah M.",
      detail: "Health Coaching Client"
    },
    {
      quote: "After years of trying quick fixes, Jill's back-to-basics philosophy finally helped me understand what my body was trying to tell me.",
      name: "Michael R.",
      detail: "Movement & Coaching Client"
    },
  ];

  return (
    <div className="bg-cream" data-testid="home-page">
      {/* Hero Section */}
      <section className="min-h-screen flex items-center pt-24 pb-16 md:pt-32 md:pb-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial="initial"
              animate="animate"
              variants={staggerContainer}
            >
              <motion.p 
                variants={fadeUp}
                className="text-terracotta font-body text-sm tracking-widest uppercase mb-6"
              >
                Slow Wellness · Deep Connection · Whole Self
              </motion.p>
              
              <motion.h1 
                variants={fadeUp}
                className="font-heading text-5xl md:text-6xl lg:text-7xl text-forest leading-[1.1] mb-6"
              >
                Come Back<br />
                <span className="italic text-terracotta">to Yourself</span>
              </motion.h1>
              
              <motion.p 
                variants={fadeUp}
                className="text-stone-600 text-lg md:text-xl font-body leading-relaxed mb-10 max-w-xl"
              >
                In a world that pushes us to optimize, hack, and override our bodies, 
                I invite you to slow down, pay attention, and reconnect with the wisdom 
                you already hold within.
              </motion.p>
              
              <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-book-btn"
                  className="bg-forest text-cream px-8 py-4 rounded-full font-body font-medium hover:bg-forest-dark transition-all duration-300 inline-flex items-center gap-2"
                >
                  Book a Session
                  <ArrowRight size={18} />
                </a>
                <Link
                  to="/services"
                  data-testid="hero-services-btn"
                  className="border-2 border-forest text-forest px-8 py-4 rounded-full font-body font-medium hover:bg-forest hover:text-cream transition-all duration-300"
                >
                  Explore Services
                </Link>
              </motion.div>
              <motion.div variants={fadeUp} className="mt-5">
                <BreathingRoomAttribution align="left" />
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative"
            >
              <div className="relative rounded-[2rem] overflow-hidden shadow-soft">
                <img
                  src="https://customer-assets.emergentagent.com/job_5bded924-5fc9-491e-80e1-89eb4978a076/artifacts/k0jcsl6p_IMG_3999.JPG"
                  alt="Jill Bennett - Grounded Wellness practitioner"
                  className="w-full h-[500px] md:h-[600px] object-cover object-center"
                  data-testid="hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forest/20 to-transparent"></div>
              </div>
              {/* Floating accent */}
              <div className="absolute -bottom-6 -left-6 bg-terracotta text-white p-6 rounded-2xl shadow-card hidden md:block">
                <p className="font-heading text-3xl">15+</p>
                <p className="font-body text-sm opacity-90">Years of Practice</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 md:py-28 bg-stone-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <p className="text-terracotta font-body text-sm tracking-widest uppercase mb-4">
              My Approach
            </p>
            <h2 className="font-heading text-4xl md:text-5xl text-forest mb-6">
              Grounded & Getting to the Roots
            </h2>
            <p className="text-stone-600 text-lg font-body leading-relaxed">
              Technology is disconnecting us from our bodies. Biohacks promise shortcuts 
              that bypass our body's wisdom. I offer something different: getting back to 
              our roots—real, grounded healing that treats your body as a guide, not a 
              problem to fix.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Three Pillars Section */}
      <section className="py-20 md:py-28" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-terracotta font-body text-sm tracking-widest uppercase mb-4">
              Three Pillars of Transformation
            </p>
            <h2 className="font-heading text-4xl md:text-5xl text-forest">
              Movement. Health. Connection.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {pillars.map((pillar, index) => (
              <motion.div
                key={pillar.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.15 }}
                data-testid={`pillar-card-${index + 1}`}
                className="bg-stone-100 p-8 md:p-10 rounded-2xl hover:shadow-card transition-all duration-500 group border border-transparent hover:border-terracotta/30"
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="font-heading text-5xl text-stone-300 group-hover:text-terracotta transition-colors">
                    {pillar.number}
                  </span>
                  <pillar.icon size={32} className="text-terracotta" />
                </div>
                <h3 className="font-heading text-2xl text-forest mb-4">
                  {pillar.title}
                </h3>
                <p className="text-stone-600 font-body leading-relaxed">
                  {pillar.description}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-center mt-12"
          >
            <Link
              to="/services"
              data-testid="explore-services-link"
              className="inline-flex items-center gap-2 text-terracotta font-body font-medium hover:gap-4 transition-all duration-300"
            >
              Learn more about my services
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-20 md:py-28 bg-forest text-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_5bded924-5fc9-491e-80e1-89eb4978a076/artifacts/2edf7smx_3.png"
                alt="Jill journaling - reflective practice"
                className="rounded-2xl w-full h-[400px] md:h-[500px] object-cover object-top"
                data-testid="about-preview-image"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <p className="text-terracotta font-body text-sm tracking-widest uppercase mb-4">
                About Jill
              </p>
              <h2 className="font-heading text-4xl md:text-5xl mb-6">
                My Own Journey<br />
                <span className="italic opacity-80">Back to Ground</span>
              </h2>
              <p className="text-cream/80 text-lg font-body leading-relaxed mb-6">
                I didn't come to this work through a straight path. A major life event 
                and identity crisis led me to question everything—and in that questioning, 
                I found a way of healing that changed my life.
              </p>
              <p className="text-cream/80 text-lg font-body leading-relaxed mb-8">
                Now I help others who've experienced setbacks—physical, emotional, or 
                spiritual—find their way back to wholeness through movement, nutrition, 
                and deep inner work.
              </p>
              <Link
                to="/about"
                data-testid="read-story-link"
                className="inline-flex items-center gap-2 bg-terracotta text-white px-8 py-4 rounded-full font-body font-medium hover:bg-terracotta-dark transition-all duration-300"
              >
                Read My Full Story
                <ArrowRight size={18} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Preview */}
      <section className="py-20 md:py-28" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-terracotta font-body text-sm tracking-widest uppercase mb-4">
              Client Stories
            </p>
            <h2 className="font-heading text-4xl md:text-5xl text-forest">
              Transformations from the Ground Up
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.15 }}
                data-testid={`testimonial-card-${index + 1}`}
                className="bg-stone-100 p-8 md:p-10 rounded-2xl"
              >
                <p className="font-heading text-2xl md:text-3xl text-forest leading-relaxed mb-8 italic">
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="font-body font-semibold text-forest">
                    {testimonial.name}
                  </p>
                  <p className="text-stone-500 font-body text-sm">
                    {testimonial.detail}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mt-12"
          >
            <Link
              to="/testimonials"
              data-testid="all-testimonials-link"
              className="inline-flex items-center gap-2 text-terracotta font-body font-medium hover:gap-4 transition-all duration-300"
            >
              Read more stories
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-stone-100">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-heading text-4xl md:text-5xl text-forest mb-6">
              Ready to Come Back<br />to Yourself?
            </h2>
            <p className="text-stone-600 text-lg font-body leading-relaxed mb-10 max-w-2xl mx-auto">
              Whether you're recovering from a setback, seeking to reconnect with your body, 
              or ready for deeper whole-self work—I'm here to walk alongside you.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-book-btn"
                className="bg-forest text-cream px-8 py-4 rounded-full font-body font-medium hover:bg-forest-dark transition-all duration-300 inline-flex items-center gap-2"
              >
                Book Your First Session
                <ArrowRight size={18} />
              </a>
              <Link
                to="/contact"
                data-testid="cta-contact-btn"
                className="border-2 border-forest text-forest px-8 py-4 rounded-full font-body font-medium hover:bg-forest hover:text-cream transition-all duration-300"
              >
                Let's Talk First
              </Link>
            </div>
            <div className="mt-6 flex justify-center">
              <BreathingRoomAttribution align="center" />
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
